import type { ChangeEvent } from "react";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import type { InputOnChangeData } from "../../components/Input/Input";
import {
  type IWarehouseApi,
  type JournalSearchInput,
  type ProductJournalEntry,
  WAREHOUSE_API_TOKEN,
} from "../../api/WarehouseApi";
import { Box } from "../../stores/Box";
import {
  type INavigationStore,
  NAVIGATION_STORE_TOKEN,
  Screens,
} from "../../stores/navigation";
import { Product } from "../../stores/Product";
import { STORE_TOKEN, type IStore } from "../../stores/store";
import { Action } from "../../types/entities";
import { ViewModel } from "../../lib/vvm";
import { BOX_ID_PATTERN, PRODUCT_ID_PATTERN } from "../../domain/entity-ids";

export type JournalActionFilter = "all" | Action;
export type JournalUserRoleFilter = "all" | "admin" | "user" | "viewer";

@injectable()
export class JournalPageViewModel extends ViewModel {
  @observable.shallow
  entries: ProductJournalEntry[] = [];

  @observable
  actionFilter: JournalActionFilter = "all";

  @observable
  productIdFilter = "";

  @observable
  boxIdFilter = "";

  @observable
  userIdFilter = "";

  @observable
  userRoleFilter: JournalUserRoleFilter = "all";

  @observable
  limitFilter = "50";

  @observable
  isLoading = false;

  @observable
  error: string | null = null;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();

    makeObservable(this);
  }

  @computed
  get hasActiveFilters(): boolean {
    return (
      this.actionFilter !== "all" ||
      this.productIdFilter.trim() !== "" ||
      this.boxIdFilter.trim() !== "" ||
      this.userIdFilter.trim() !== "" ||
      this.userRoleFilter !== "all" ||
      this.limitFilter.trim() !== "50"
    );
  }

  protected onViewMounted(): void {
    void this.loadEntries();
  }

  @action.bound
  onActionFilterChange(event: ChangeEvent<HTMLSelectElement>): void {
    this.actionFilter = event.target.value as JournalActionFilter;
    this.error = null;
  }

  @action.bound
  onProductIdFilterChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.productIdFilter = data.value || event.target.value;
    this.error = null;
  }

  @action.bound
  onBoxIdFilterChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.boxIdFilter = data.value || event.target.value;
    this.error = null;
  }

  @action.bound
  onUserIdFilterChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.userIdFilter = data.value || event.target.value;
    this.error = null;
  }

  @action.bound
  onUserRoleFilterChange(event: ChangeEvent<HTMLSelectElement>): void {
    this.userRoleFilter = event.target.value as JournalUserRoleFilter;
    this.error = null;
  }

  @action.bound
  onLimitFilterChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.limitFilter = data.value || event.target.value;
    this.error = null;
  }

  @action.bound
  async applyFilters(): Promise<void> {
    await this.loadEntries();
  }

  @action.bound
  clearFilters(): void {
    this.actionFilter = "all";
    this.productIdFilter = "";
    this.boxIdFilter = "";
    this.userIdFilter = "";
    this.userRoleFilter = "all";
    this.limitFilter = "50";
    this.error = null;
    void this.loadEntries();
  }

  @action.bound
  async refresh(): Promise<void> {
    await this.loadEntries();
  }

  @action.bound
  async loadEntries(): Promise<void> {
    const limit = this.parseLimit();
    if (limit === null) {
      this.error = "limit должен быть числом от 1 до 500";
      return;
    }

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const payload: JournalSearchInput = { limit };

      if (this.actionFilter !== "all") {
        payload.action = this.actionFilter;
      }

      const productId = this.productIdFilter.trim();
      if (productId) {
        payload.productId = productId;
      }

      const boxId = this.boxIdFilter.trim();
      if (boxId) {
        payload.fromTo = boxId;
      }

      const userId = this.userIdFilter.trim();
      if (userId) {
        payload.userId = userId;
      }

      if (this.userRoleFilter !== "all") {
        payload.userRole = this.userRoleFilter;
      }

      const entries = await this.api.searchJournalEntries(payload);
      runInAction(() => {
        this.entries = entries;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось загрузить записи журнала";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  async openProduct(entry: ProductJournalEntry): Promise<void> {
    const productId = entry.productId.trim();

    if (!PRODUCT_ID_PATTERN.test(productId)) {
      this.error = "Некорректный productId в записи журнала";
      return;
    }

    await this.openProductById(productId);
  }

  @action.bound
  async openBox(entry: ProductJournalEntry): Promise<void> {
    const boxId = entry.fromTo.trim();

    if (!BOX_ID_PATTERN.test(boxId)) {
      this.error = "Некорректный boxId в записи журнала";
      return;
    }

    await this.openBoxById(boxId);
  }

  @action.bound
  async openEntry(entry: ProductJournalEntry): Promise<void> {
    const productId = entry.productId.trim();
    if (PRODUCT_ID_PATTERN.test(productId)) {
      const isProductOpened = await this.openProductById(productId);
      if (isProductOpened) {
        return;
      }
    }

    const boxId = entry.fromTo.trim();
    if (BOX_ID_PATTERN.test(boxId)) {
      await this.openBoxById(boxId);
      return;
    }

    this.error =
      "В записи журнала нет корректных ссылок на продукт или коробку";
  }

  private async openProductById(productId: string): Promise<boolean> {
    const existing = this.store.products.find((item) => item.id === productId);
    if (existing) {
      this.navigationStore.setScreen(Screens.Product, existing);
      return true;
    }

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const product = await this.api.getProductById(productId);
      if (!product) {
        runInAction(() => {
          this.error = "Продукт из записи журнала не найден";
        });
        return false;
      }

      const model = new Product(product);
      this.store.addProduct(model);
      this.navigationStore.setScreen(Screens.Product, model);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Не удалось открыть продукт";
      });
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  private async openBoxById(boxId: string): Promise<boolean> {
    const existing = this.store.boxes.find((item) => item.id === boxId);
    if (existing) {
      this.navigationStore.setScreen(Screens.Box, existing);
      return true;
    }

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const box = await this.api.getBoxById(boxId);
      if (!box) {
        runInAction(() => {
          this.error = "Коробка из записи журнала не найдена";
        });
        return false;
      }

      const model = new Box(box);
      this.store.addBox(model);
      this.navigationStore.setScreen(Screens.Box, model);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Не удалось открыть коробку";
      });
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  private parseLimit(): number | null {
    const raw = this.limitFilter.trim();
    if (!raw) {
      return 50;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 500) {
      return null;
    }

    return Math.floor(parsed);
  }
}
